import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Field, PageHeader, Panel } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import TeamPanel from "@/components/TeamPanel";

const WS_FIELDS = [["name", "Name"], ["website", "Website"], ["industry", "Industry"], ["country", "Country"], ["timezone", "Time zone"], ["currency", "Currency"], ["logo_url", "Logo URL"]];

function WorkspaceForm() {
  const { current, path, reloadWorkspaces, role } = useWorkspace();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm({ ...current, markets: current?.markets || [] }), [current]);
  const canEdit = ["owner", "admin"].includes(role);
  const save = async () => {
    setBusy(true);
    const body = Object.fromEntries(WS_FIELDS.map(([k]) => [k, form[k] || null]));
    body.name = form.name; body.markets = form.markets;
    try { await api.patch(path(""), body); await reloadWorkspaces(); toast.success("Workspace updated"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Workspace" eyebrow={`Slug · ${current?.slug}`} testId="settings-workspace">
      <div className="grid md:grid-cols-2 gap-4">
        {WS_FIELDS.map(([k, l]) => <Field key={k} label={l}><input data-testid={`settings-ws-${k}`} disabled={!canEdit} className="cq-input" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></Field>)}
        <Field label="Markets (comma separated)"><input data-testid="settings-ws-markets" disabled={!canEdit} className="cq-input" value={(form.markets || []).join(", ")} onChange={(e) => setForm({ ...form, markets: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-[#7C8BAD]">
        <span>Created {fmtDate(current?.created_at, { dateStyle: "medium" })} · Updated {fmtDate(current?.updated_at, { dateStyle: "medium" })}</span>
        {canEdit && <button data-testid="settings-ws-save" disabled={busy} onClick={save} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>}
      </div>
    </Panel>
  );
}

export default function Settings() {
  const { workspaces, role, path, reloadWorkspaces, switchTo, current } = useWorkspace();
  const [confirm, setConfirm] = useState(false);
  const archive = async () => {
    try { await api.delete(path("")); const other = workspaces.find((w) => w.id !== current.id); switchTo(other.id); await reloadWorkspaces(); toast.success("Workspace archived"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Configure" title="Workspace settings" description="Workspace profile, team and access. Your own profile and security live under Account."
        actions={<Link to="/account?tab=profile" data-testid="settings-open-account" className="cq-btn cq-btn-ghost">My account</Link>} />
      <WorkspaceForm />
      <TeamPanel />
      {role === "owner" && (
        <Panel title="Danger zone" testId="settings-danger">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3"><p className="text-sm text-[#AAB6CC]">Archive this workspace. Data is soft-deleted and retained for audit.</p>
            <button data-testid="settings-archive-workspace" disabled={workspaces.length < 2} onClick={() => setConfirm(true)} className="cq-btn cq-btn-danger">Archive workspace</button></div>
          {workspaces.length < 2 && <p className="mt-2 text-xs text-[#7C8BAD]">You cannot archive your only workspace.</p>}
        </Panel>)}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} danger title={`Archive ${current?.name}?`} description="Members lose access immediately. This is recorded in the audit log." confirmLabel="Archive" onConfirm={archive} />
    </div>
  );
}
